import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Row, Empty, Alert, Select, Table, Tabs, Tooltip as AntTooltip, Drawer } from "antd";
import { QuestionCircleOutlined, BookOutlined } from "@ant-design/icons";
import {
  LineChart, Line, BarChart, Bar, ComposedChart, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend, LabelList,
} from "recharts";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { analyticsApi } from "../../services/api";
import GaFilterBar, { ALL_SEGMENT_VALUES, DEFAULT_GA_FILTER, defaultRange } from "./components/GaFilterBar";
import { ChartError, ChartSkeleton, MetricLabel, Shimmer } from "./components/ChartParts";
import ComparePeriods from "./components/ComparePeriods";
import { compareRangeFor } from "./gaCompare";
import {
  LEGEND_STYLE,
  MAX_CONCURRENT_REQUESTS,
  METRIC_HINTS,
  createRequestPool,
  fmtDuration,
  fmtNumber,
  fmtPct,
  pctLabel,
  xLabel,
  yLabel,
} from "./gaFormat";

dayjs.extend(utc);
dayjs.extend(timezone);

// GA4 property's reporting timezone — must match GA Admin → Property → Property details.
// Activity-by-hour rows arrive bucketed in this timezone; the viewer can re-bucket
// into any IANA timezone (UTC, Asia/Kolkata, …) via dayjs on the client.
const PROPERTY_TIMEZONE = "Europe/London";

const HOUR_TZ_OPTIONS = [
  { label: "UTC", value: "UTC" },
  { label: "London (UK)", value: "Europe/London" },
  { label: "India (IST)", value: "Asia/Kolkata" },
];

const PIE_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16", "#f97316", "#14b8a6", "#a855f7"];

const CHART_HINTS = {
  timeSeries: "Daily counts of active users, sessions, and views. Spot trends, weekday-vs-weekend patterns, or unusual spikes and dips.",
  monthlyTrend: "6-month trend of New users vs Active users, aggregated per calendar month. Always shows the last 6 months — ignores the date-range filter above. Tenant and platform filters still apply.",
  clientBreakdown: "Active users for each tenant in the date range (top 10, sorted by active users). A tenant's web and app traffic is combined even when they arrive under different labels (name vs subdomain); a person using both web and app under different labels may count twice.",
  moduleGroupPie: "Share of total views across the major product areas. Web module groups are derived from the page path; app module groups from the app screen name.",
  deviceBreakdown: "Sessions split into web (Desktop, Tablet, Mobile browser) and app (iOS, Android). Reveals where users access SafetyQube from.",
  clientDeviceBreakdown: "Top 10 clients (by total sessions) split by platform and device. Useful to spot tenant-specific web vs app usage.",
  moduleGroupBar: "Same module groups as the pie above, shown as raw view totals. Good for comparing magnitudes between groups rather than proportions.",
  activityByHour: "Active users aggregated across the date range, bucketed by the hour of day they occurred (in the property's timezone). Reveals peak usage hours.",
  activityPerClient: "Heatmap of active users for the top 5 clients (by total active users) across each hour of the day. Darker cells = higher activity. Color scale is shared across all clients so cells are comparable to each other.",
  engagementByClient: "Engagement rate for each client (top 30 by sessions). High engagement rate means users are spending time, viewing multiple pages, or triggering conversions — not just bouncing.",
  clientModulePivot: "Cross-tab showing which clients use which product areas. Each cell has views and active users for that client × module group combination. Sortable on any column.",
  topModules: "Most-used pages and app screens, classified into module names (web via page path rules, app via screen name). Views and active users are summed across all pages/screens that map to the same module name.",
};

function bucketActivityByHour(rows, sourceTz, targetTz) {
  const buckets = [];
  for (let h = 0; h < 24; h++) {
    buckets.push({ hour: h, activeUsers: 0, sessions: 0 });
  }
  for (const r of rows || []) {
    const dh = r.dateHour;
    if (!dh || dh.length !== 10) continue;
    const isoLocal = `${dh.slice(0, 4)}-${dh.slice(4, 6)}-${dh.slice(6, 8)}T${dh.slice(8, 10)}:00:00`;
    const m = dayjs.tz(isoLocal, sourceTz);
    if (!m.isValid()) continue;
    const targetHour = m.tz(targetTz).hour();
    const bucket = buckets[targetHour];
    bucket.activeUsers += Number(r.activeUsers || 0);
    bucket.sessions += Number(r.sessions || 0);
  }
  return buckets;
}

function bucketActivityPerClient(rows, sourceTz, targetTz, topN = 5) {
  const totals = new Map();
  for (const r of rows || []) {
    totals.set(r.clientName, (totals.get(r.clientName) || 0) + Number(r.activeUsers || 0));
  }
  const topClients = [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([c]) => c);
  const topSet = new Set(topClients);

  const grid = {};
  for (const c of topClients) {
    grid[c] = new Array(24).fill(0);
  }
  for (const r of rows || []) {
    if (!topSet.has(r.clientName)) continue;
    const dh = r.dateHour;
    if (!dh || dh.length !== 10) continue;
    const isoLocal = `${dh.slice(0, 4)}-${dh.slice(4, 6)}-${dh.slice(6, 8)}T${dh.slice(8, 10)}:00:00`;
    const m = dayjs.tz(isoLocal, sourceTz);
    if (!m.isValid()) continue;
    const h = m.tz(targetTz).hour();
    grid[r.clientName][h] += Number(r.activeUsers || 0);
  }

  let max = 0;
  for (const c of topClients) {
    for (const v of grid[c]) {
      if (v > max) max = v;
    }
  }
  return { clients: topClients, grid, max };
}

function withPercent(data, key) {
  const total = data.reduce((s, d) => s + (Number(d[key]) || 0), 0);
  return data.map((d) => ({
    ...d,
    _pct: total > 0 ? (Number(d[key]) || 0) / total : 0,
  }));
}

const CHART_FETCHERS = {
  overview: (p) => analyticsApi.getOverview(p),
  usersOverTime: (p) => analyticsApi.getUsersOverTime(p),
  clientBreakdown: (p) => analyticsApi.getClientBreakdown({ ...p, limit: 10 }),
  deviceBreakdown: (p) => analyticsApi.getDeviceBreakdown(p),
  clientDeviceBreakdown: (p) => analyticsApi.getClientDeviceBreakdown({ ...p, limit: 10 }),
  topModules: (p) => analyticsApi.getTopModules({ ...p, limit: 25 }),
  moduleGroups: (p) => analyticsApi.getModuleGroupBreakdown(p),
  activityByHour: (p) => analyticsApi.getActivityByHour(p),
  activityPerClient: (p) => analyticsApi.getActivityByHourPerClient(p),
  monthlyTrend: (p) => analyticsApi.getMonthlyTrend(p),
  clientModulePivot: (p) => analyticsApi.getClientModulePivot(p),
  engagementByClient: (p) => analyticsApi.getEngagementByClient({ ...p, limit: 30 }),
};

const CHART_DEFAULTS = {
  overview: null,
  usersOverTime: [],
  clientBreakdown: [],
  deviceBreakdown: [],
  clientDeviceBreakdown: { devices: [], rows: [] },
  topModules: [],
  moduleGroups: [],
  activityByHour: [],
  activityPerClient: [],
  monthlyTrend: [],
  clientModulePivot: { groups: [], rows: [] },
  engagementByClient: [],
};

export default function GoogleAnalyticsPage() {
  const [gaFilter, setGaFilter] = useState(() => ({ ...DEFAULT_GA_FILTER, range: defaultRange() }));

  // Per-chart fetch state — each chart loads, errors, and retries on its own
  const [charts, setCharts] = useState(() => {
    const init = {};
    for (const key of Object.keys(CHART_FETCHERS)) {
      init[key] = { loading: true, error: null, data: CHART_DEFAULTS[key] };
    }
    return init;
  });
  const requestSeq = useRef(0);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [hourTz, setHourTz] = useState(PROPERTY_TIMEZONE);

  const [needsReauth, setNeedsReauth] = useState(false);
  const [gaPermissionDenied, setGaPermissionDenied] = useState(false);

  // Keyed on the fields that reach the API so compare-only edits don't reload the Overview tab.
  const { range, platforms, hideUntagged, tenants, tenantMode } = gaFilter;
  const params = useMemo(() => {
    const p = {
      startDate: range[0].format("YYYY-MM-DD"),
      endDate: range[1].format("YYYY-MM-DD"),
    };
    if (platforms.length < ALL_SEGMENT_VALUES.length) p.platforms = platforms.join(",");
    if (!hideUntagged) p.includeUntagged = "1";
    if (tenants.length > 0) {
      p.tenantMode = tenantMode;
      p.tenants = tenants.join(",");
    }
    return p;
  }, [range, platforms, hideUntagged, tenants, tenantMode]);

  const [activeTab, setActiveTab] = useState("overview");
  const [compareOpened, setCompareOpened] = useState(false);
  const compareRange = useMemo(() => compareRangeFor(gaFilter), [gaFilter]);
  const compareParams = useMemo(
    () => ({ ...params, startDate: compareRange[0].format("YYYY-MM-DD"), endDate: compareRange[1].format("YYYY-MM-DD") }),
    [params, compareRange],
  );

  const handleAuthError = useCallback((status) => {
    if (status === 428) setNeedsReauth(true);
    else if (status === 403) setGaPermissionDenied(true);
  }, []);

  const changeTab = (key) => {
    setActiveTab(key);
    if (key === "compare") setCompareOpened(true);
  };

  const fetchChart = useCallback((key, fetchParams, seq, run = (fn) => fn()) => {
    setCharts((prev) => ({ ...prev, [key]: { ...prev[key], loading: true, error: null } }));
    run(() => CHART_FETCHERS[key](fetchParams))
      .then((res) => {
        if (seq !== requestSeq.current) return;
        setNeedsReauth(false);
        setCharts((prev) => ({ ...prev, [key]: { loading: false, error: null, data: res.data } }));
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        if (err?.response?.status === 428) setNeedsReauth(true);
        else if (err?.response?.status === 403) setGaPermissionDenied(true);
        const msg = err?.response?.data?.error || err?.message || "Request failed";
        setCharts((prev) => ({ ...prev, [key]: { ...prev[key], loading: false, error: msg } }));
      });
  }, []);

  useEffect(() => {
    requestSeq.current += 1;
    const seq = requestSeq.current;
    const run = createRequestPool(MAX_CONCURRENT_REQUESTS);
    Object.keys(CHART_FETCHERS).forEach((key) => fetchChart(key, params, seq, run));
  }, [params, fetchChart]);

  const retryChart = useCallback(
    (key) => fetchChart(key, params, requestSeq.current),
    [fetchChart, params],
  );

  const overview = charts.overview.data;
  const usersOverTime = charts.usersOverTime.data ?? CHART_DEFAULTS.usersOverTime;
  const clientBreakdown = charts.clientBreakdown.data ?? CHART_DEFAULTS.clientBreakdown;
  const deviceBreakdown = charts.deviceBreakdown.data ?? CHART_DEFAULTS.deviceBreakdown;
  const clientDeviceBreakdown = charts.clientDeviceBreakdown.data ?? CHART_DEFAULTS.clientDeviceBreakdown;
  const topModules = charts.topModules.data ?? CHART_DEFAULTS.topModules;
  const moduleGroups = charts.moduleGroups.data ?? CHART_DEFAULTS.moduleGroups;
  const activityByHour = charts.activityByHour.data ?? CHART_DEFAULTS.activityByHour;
  const activityPerClient = charts.activityPerClient.data ?? CHART_DEFAULTS.activityPerClient;
  const monthlyTrend = charts.monthlyTrend.data ?? CHART_DEFAULTS.monthlyTrend;
  const clientModulePivot = charts.clientModulePivot.data ?? CHART_DEFAULTS.clientModulePivot;
  const engagementByClient = charts.engagementByClient.data ?? CHART_DEFAULTS.engagementByClient;

  const activityByHourBucketed = useMemo(
    () => bucketActivityByHour(activityByHour, PROPERTY_TIMEZONE, hourTz),
    [activityByHour, hourTz],
  );

  const perClientHeatmap = useMemo(
    () => bucketActivityPerClient(activityPerClient, PROPERTY_TIMEZONE, hourTz, 7),
    [activityPerClient, hourTz],
  );

  const engagementByClientSorted = useMemo(
    () => [...engagementByClient].sort((a, b) => b.engagementRate - a.engagementRate),
    [engagementByClient],
  );

  const clientBreakdownPareto = useMemo(() => {
    const total = clientBreakdown.reduce((s, d) => s + (Number(d.activeUsers) || 0), 0);
    let running = 0;
    return clientBreakdown.map((d) => {
      const v = Number(d.activeUsers) || 0;
      running += v;
      return {
        ...d,
        _pct: total > 0 ? v / total : 0,
        _cumPct: total > 0 ? running / total : 0,
      };
    });
  }, [clientBreakdown]);
  const deviceBreakdownPct = useMemo(() => withPercent(deviceBreakdown, "sessions"), [deviceBreakdown]);
  const moduleGroupsPct = useMemo(() => withPercent(moduleGroups, "views"), [moduleGroups]);

  const clientDeviceColumns = useMemo(() => {
    const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
    const cols = [
      {
        title: "Client",
        dataIndex: "clientName",
        key: "clientName",
        fixed: "left",
        width: 160,
        ellipsis: true,
        sorter: (a, b) => (a.clientName || "").localeCompare(b.clientName || ""),
      },
    ];
    for (const device of clientDeviceBreakdown.devices || []) {
      cols.push({
        title: cap(device),
        key: device,
        align: "right",
        width: 100,
        sorter: (a, b) => (a.byDevice?.[device]?.sessions || 0) - (b.byDevice?.[device]?.sessions || 0),
        render: (_, row) => fmtNumber(row.byDevice?.[device]?.sessions || 0),
      });
    }
    cols.push({
      title: "Total",
      dataIndex: "totalSessions",
      key: "totalSessions",
      align: "right",
      width: 100,
      sorter: (a, b) => a.totalSessions - b.totalSessions,
      defaultSortOrder: "descend",
      render: fmtNumber,
    });
    return cols;
  }, [clientDeviceBreakdown.devices]);

  const pivotColumns = useMemo(() => {
    const cols = [
      {
        title: "Client",
        dataIndex: "client",
        key: "client",
        fixed: "left",
        width: 160,
        sorter: (a, b) => (a.client || "").localeCompare(b.client || ""),
      },
      {
        title: "Total Views",
        dataIndex: "totalViews",
        key: "totalViews",
        align: "right",
        width: 110,
        sorter: (a, b) => a.totalViews - b.totalViews,
        defaultSortOrder: "descend",
        render: fmtNumber,
      },
    ];
    for (const group of clientModulePivot.groups || []) {
      cols.push({
        title: group,
        children: [
          {
            title: "Views",
            key: `${group}-views`,
            align: "right",
            width: 90,
            render: (_, row) => fmtNumber(row.byGroup?.[group]?.views || 0),
            sorter: (a, b) => (a.byGroup?.[group]?.views || 0) - (b.byGroup?.[group]?.views || 0),
          },
          {
            title: "Users",
            key: `${group}-users`,
            align: "right",
            width: 90,
            render: (_, row) => fmtNumber(row.byGroup?.[group]?.activeUsers || 0),
            sorter: (a, b) => (a.byGroup?.[group]?.activeUsers || 0) - (b.byGroup?.[group]?.activeUsers || 0),
          },
        ],
      });
    }
    return cols;
  }, [clientModulePivot.groups]);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Google Analytics</h1>
      </div>

      {needsReauth && (
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="Google Analytics access not available"
          description="Your stored Google Analytics authorization is missing or expired. Sign out and sign back in to grant access."
        />
      )}

      {gaPermissionDenied && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message="No Google Analytics permission"
          description="Your Google account does not have access to this Analytics property. Ask a GA admin to add your email as a Viewer at the property level in Google Analytics, then sign out and back in."
        />
      )}

      <Card size="small" className="mb-4">
        <GaFilterBar value={gaFilter} onApply={setGaFilter} showCompare={activeTab === "compare"} />
      </Card>

      <Tabs
        activeKey={activeTab}
        onChange={changeTab}
        items={[
          {
            key: "overview",
            label: "Overview",
            children: (
              <div>
            <Row gutter={[8, 8]} justify="center" className="mb-4">
              {[
                { label: "Total Users", hint: METRIC_HINTS.totalUsers, value: fmtNumber(overview?.totalUsers) },
                { label: "Active Users", hint: METRIC_HINTS.activeUsers, value: fmtNumber(overview?.activeUsers) },
                { label: "New Users", hint: METRIC_HINTS.newUsers, value: fmtNumber(overview?.newUsers) },
                { label: "Sessions", hint: METRIC_HINTS.sessions, value: fmtNumber(overview?.sessions) },
                { label: "Views", hint: METRIC_HINTS.views, value: fmtNumber(overview?.screenPageViews) },
                { label: "Engagement", hint: METRIC_HINTS.engagementRate, value: fmtPct(overview?.engagementRate) },
                { label: "Bounce", hint: METRIC_HINTS.bounceRate, value: fmtPct(overview?.bounceRate) },
                { label: "Avg Session", hint: METRIC_HINTS.avgSessionDuration, value: fmtDuration(overview?.averageSessionDuration) },
              ].map((kpi) => (
                <Col key={kpi.label} xs={12} sm={8} md={6} lg={3} className="flex">
                  <Card
                    size="small"
                    className="flex-1 text-center"
                    styles={{ body: { padding: "8px 10px" } }}
                  >
                    <div className="text-[11px] text-slate-500 mb-1">
                      <MetricLabel title={kpi.label} hint={kpi.hint} />
                    </div>
                    <div className="text-[18px] font-semibold text-slate-900 leading-[1.2] min-h-[22px]">
                      {charts.overview.loading
                        ? <Shimmer className="h-[18px] w-14 mx-auto" />
                        : kpi.value}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>

            {charts.overview.error && !charts.overview.loading && (
              <div className="text-center -mt-2 mb-4 text-[12px] text-red-600">
                Failed to load overview metrics —{" "}
                <a onClick={() => retryChart("overview")} className="cursor-pointer">retry</a>
              </div>
            )}

            <Row gutter={[16, 16]} className="items-stretch">
              <Col xs={24} lg={12} className="flex flex-col">
                <Card title={<MetricLabel title="Active users, Sessions & Views over time" hint={CHART_HINTS.timeSeries} />} size="small" className="flex-1">
                  {charts.usersOverTime.loading ? (
                    <ChartSkeleton type="line" height={300} />
                  ) : charts.usersOverTime.error ? (
                    <ChartError message={charts.usersOverTime.error} onRetry={() => retryChart("usersOverTime")} height={300} />
                  ) : usersOverTime.length === 0 ? (
                    <Empty description="No data" />
                  ) : (
                    <ResponsiveContainer width="100%" height={320}>
                      <LineChart data={usersOverTime} margin={{ top: 10, right: 16, bottom: 32, left: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 11 }}
                          height={52}
                          label={xLabel("Date")}
                          tickFormatter={(d) => dayjs(d).format("MMM D")}
                          minTickGap={16}
                        />
                        <YAxis tick={{ fontSize: 11 }} label={yLabel("Count")} />
                        <Tooltip labelFormatter={(d) => dayjs(d).format("MMM D, YYYY")} />
                        <Legend wrapperStyle={LEGEND_STYLE} />
                        <Line type="monotone" dataKey="activeUsers" name="Active users" stroke="#3b82f6" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="sessions" name="Sessions" stroke="#f59e0b" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="views" name="Views" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={12} className="flex flex-col">
                <Card
                  title={<MetricLabel title="New vs Active users — last 6 months" hint={CHART_HINTS.monthlyTrend} />}
                  size="small"
                  className="flex-1"
                  extra={
                    <span className="text-[12px] text-slate-500 inline-flex items-center gap-1">
                      fixed window
                      <AntTooltip title="This chart uses a fixed window and does not change with the date range picker.">
                        <QuestionCircleOutlined className="cursor-help" />
                      </AntTooltip>
                    </span>
                  }
                >
                  {charts.monthlyTrend.loading ? (
                    <ChartSkeleton type="line" height={300} />
                  ) : charts.monthlyTrend.error ? (
                    <ChartError message={charts.monthlyTrend.error} onRetry={() => retryChart("monthlyTrend")} height={300} />
                  ) : monthlyTrend.length === 0 ? (
                    <Empty description="No data" />
                  ) : (
                    <ResponsiveContainer width="100%" height={320}>
                      <LineChart data={monthlyTrend} margin={{ top: 10, right: 16, bottom: 32, left: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="yearMonth" tick={{ fontSize: 11 }} height={52} label={xLabel("Month")} />
                        <YAxis tick={{ fontSize: 11 }} label={yLabel("Users")} />
                        <Tooltip formatter={(v) => Number(v).toLocaleString()} />
                        <Legend wrapperStyle={LEGEND_STYLE} />
                        <Line type="monotone" dataKey="newUsers" name="New users" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="activeUsers" name="Active users" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={12} className="flex flex-col">
                <Card title={<MetricLabel title="Active users by Client (share of total)" hint={CHART_HINTS.clientBreakdown} />} size="small" className="flex-1">
                  {charts.clientBreakdown.loading ? (
                    <ChartSkeleton type="pie" height={340} />
                  ) : charts.clientBreakdown.error ? (
                    <ChartError message={charts.clientBreakdown.error} onRetry={() => retryChart("clientBreakdown")} height={340} />
                  ) : clientBreakdownPareto.length === 0 ? (
                    <Empty description="No data" />
                  ) : (
                    <ResponsiveContainer width="100%" height={340}>
                      <PieChart>
                        <Pie
                          data={clientBreakdownPareto}
                          dataKey="activeUsers"
                          nameKey="clientName"
                          innerRadius={65}
                          outerRadius={115}
                          paddingAngle={2}
                          label={({ percent }) => percent >= 0.04 ? `${(percent * 100).toFixed(0)}%` : ""}
                          labelLine={false}
                        >
                          {clientBreakdownPareto.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(v, _name, item) => {
                            const pct = item?.payload?._pct;
                            const pctStr = pct != null ? ` (${(pct * 100).toFixed(1)}%)` : "";
                            return `${Number(v).toLocaleString()}${pctStr}`;
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={12} className="flex flex-col">
                <Card title={<MetricLabel title="Views by Module Group" hint={CHART_HINTS.moduleGroupPie} />} size="small" className="flex-1">
                  {charts.moduleGroups.loading ? (
                    <ChartSkeleton type="pie" height={340} />
                  ) : charts.moduleGroups.error ? (
                    <ChartError message={charts.moduleGroups.error} onRetry={() => retryChart("moduleGroups")} height={340} />
                  ) : moduleGroups.length === 0 ? (
                    <Empty description="No data" />
                  ) : (
                    <ResponsiveContainer width="100%" height={340}>
                      <PieChart>
                        <Pie
                          data={moduleGroups}
                          dataKey="views"
                          nameKey="group"
                          innerRadius={65}
                          outerRadius={115}
                          paddingAngle={2}
                          label={({ percent }) => percent >= 0.04 ? `${(percent * 100).toFixed(0)}%` : ""}
                          labelLine={false}
                        >
                          {moduleGroups.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => Number(v).toLocaleString()} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={12} className="flex flex-col">
                <Card title={<MetricLabel title="Sessions by platform & device" hint={CHART_HINTS.deviceBreakdown} />} size="small" className="flex-1">
                  {charts.deviceBreakdown.loading ? (
                    <ChartSkeleton type="bar" height={360} />
                  ) : charts.deviceBreakdown.error ? (
                    <ChartError message={charts.deviceBreakdown.error} onRetry={() => retryChart("deviceBreakdown")} height={360} />
                  ) : deviceBreakdownPct.length === 0 ? (
                    <Empty description="No data" />
                  ) : (
                    <ResponsiveContainer width="100%" height={360}>
                      <BarChart data={deviceBreakdownPct} margin={{ top: 24, right: 16, bottom: 24, left: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="device" tick={{ fontSize: 11 }} height={52} label={xLabel("Platform / device")} />
                        <YAxis tick={{ fontSize: 11 }} label={yLabel("Sessions")} />
                        <Tooltip formatter={(v) => Number(v).toLocaleString()} />
                        <Bar dataKey="sessions" name="Sessions" fill="#10b981" radius={[4, 4, 0, 0]}>
                          <LabelList dataKey="_pct" position="top" formatter={pctLabel} style={{ fontSize: 11, fill: "#475569" }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={12} className="flex flex-col">
                <Card title={<MetricLabel title="Top 10 clients by platform & device" hint={CHART_HINTS.clientDeviceBreakdown} />} size="small" className="flex-1">
                  {charts.clientDeviceBreakdown.loading ? (
                    <ChartSkeleton type="table" />
                  ) : charts.clientDeviceBreakdown.error ? (
                    <ChartError message={charts.clientDeviceBreakdown.error} onRetry={() => retryChart("clientDeviceBreakdown")} />
                  ) : (clientDeviceBreakdown.rows || []).length === 0 ? (
                    <Empty description="No data" />
                  ) : (
                    <Table
                      rowKey="clientName"
                      dataSource={clientDeviceBreakdown.rows}
                      columns={clientDeviceColumns}
                      size="small"
                      pagination={false}
                      scroll={{ x: "max-content", y: 320 }}
                    />
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={14} className="flex flex-col">
                <Card title={<MetricLabel title="Views by Module Group (totals)" hint={CHART_HINTS.moduleGroupBar} />} size="small" className="flex-1">
                  {charts.moduleGroups.loading ? (
                    <ChartSkeleton type="bar" height={280} />
                  ) : charts.moduleGroups.error ? (
                    <ChartError message={charts.moduleGroups.error} onRetry={() => retryChart("moduleGroups")} height={280} />
                  ) : moduleGroupsPct.length === 0 ? (
                    <Empty description="No data" />
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={moduleGroupsPct} margin={{ top: 24, right: 16, bottom: 28, left: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="group" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={78} label={xLabel("Module group")} />
                        <YAxis tick={{ fontSize: 11 }} label={yLabel("Views")} />
                        <Tooltip formatter={(v) => Number(v).toLocaleString()} />
                        <Bar dataKey="views" name="Views" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                          <LabelList dataKey="_pct" position="top" formatter={pctLabel} style={{ fontSize: 10, fill: "#475569" }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={10} className="flex flex-col">
                <Card title={<MetricLabel title="Top modules" hint={CHART_HINTS.topModules} />} size="small" className="flex-1">
                  {charts.topModules.loading ? (
                    <ChartSkeleton type="table" />
                  ) : charts.topModules.error ? (
                    <ChartError message={charts.topModules.error} onRetry={() => retryChart("topModules")} />
                  ) : (
                  <Table
                    rowKey="title"
                    dataSource={topModules}
                    size="small"
                    pagination={{ pageSize: 5, showSizeChanger: false, size: "small" }}
                    scroll={{ y: undefined }}
                    columns={[
                      { title: "Module", dataIndex: "title", ellipsis: true },
                      {
                        title: "Views", dataIndex: "views", align: "right", width: 110,
                        sorter: (a, b) => a.views - b.views,
                        render: fmtNumber,
                      },
                      {
                        title: "Active users", dataIndex: "activeUsers", align: "right", width: 120,
                        sorter: (a, b) => a.activeUsers - b.activeUsers,
                        render: fmtNumber,
                      },
                    ]}
                  />
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={10} className="flex flex-col">
                <Card
                  title={<MetricLabel title="Active users by hour" hint={CHART_HINTS.activityByHour} />}
                  size="small"
                  className="flex-1"
                  extra={
                    <Select
                      size="small"
                      value={hourTz}
                      onChange={setHourTz}
                      className="w-[200px]"
                      options={HOUR_TZ_OPTIONS}
                    />
                  }
                >
                  {charts.activityByHour.loading ? (
                    <ChartSkeleton type="bar" height={280} />
                  ) : charts.activityByHour.error ? (
                    <ChartError message={charts.activityByHour.error} onRetry={() => retryChart("activityByHour")} height={280} />
                  ) : activityByHourBucketed.every((b) => b.activeUsers === 0) ? (
                    <Empty description="No data" />
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={activityByHourBucketed} margin={{ top: 10, right: 16, bottom: 24, left: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                          dataKey="hour"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(h) => `${h}:00`}
                          interval={1}
                          height={52}
                          label={xLabel(`Hour of day (${hourTz})`)}
                        />
                        <YAxis tick={{ fontSize: 11 }} label={yLabel("Active users")} />
                        <Tooltip
                          labelFormatter={(h) => `${h}:00`}
                          formatter={(v) => Number(v).toLocaleString()}
                        />
                        <Bar dataKey="activeUsers" name="Active users" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={14} className="flex flex-col">
                <Card
                  title={<MetricLabel title="Active users by hour — top 7 clients" hint={CHART_HINTS.activityPerClient} />}
                  size="small"
                  className="flex-1"
                  extra={
                    <Select
                      size="small"
                      value={hourTz}
                      onChange={setHourTz}
                      className="w-[200px]"
                      options={HOUR_TZ_OPTIONS}
                    />
                  }
                >
                  {charts.activityPerClient.loading ? (
                    <ChartSkeleton type="heatmap" height={280} />
                  ) : charts.activityPerClient.error ? (
                    <ChartError message={charts.activityPerClient.error} onRetry={() => retryChart("activityPerClient")} height={280} />
                  ) : perClientHeatmap.clients.length === 0 || perClientHeatmap.max === 0 ? (
                    <Empty description="No data" />
                  ) : (
                    <div className="overflow-x-auto">
                      <div className="text-[11px] text-slate-600 text-center mb-1">
                        Hour of day ({hourTz}) →
                      </div>
                      <table className="ga-heatmap">
                        <thead>
                          <tr>
                            <th>Client</th>
                            {Array.from({ length: 24 }, (_, h) => (
                              <th key={h}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {perClientHeatmap.clients.map((client) => (
                            <tr key={client}>
                              <td>{client}</td>
                              {perClientHeatmap.grid[client].map((v, h) => {
                                const intensity = perClientHeatmap.max > 0 ? v / perClientHeatmap.max : 0;
                                const lightness = 96 - intensity * 50;
                                const bg = v === 0 ? "#f8fafc" : `hsl(217, 91%, ${lightness}%)`;
                                const color = intensity > 0.55 ? "#fff" : "#1e293b";
                                return (
                                  <td
                                    key={h}
                                    title={`${client} · ${h}:00 · ${v.toLocaleString()} active users`}
                                    style={{ "--cell-bg": bg, "--cell-color": color }}
                                  >
                                    {v > 0 ? v.toLocaleString() : ""}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </Col>

              <Col xs={24}>
                <Card
                  title={<MetricLabel title="Engagement rate by Client" hint={CHART_HINTS.engagementByClient} />}
                  size="small"
                  extra={<span className="text-[12px] text-slate-500">Top {engagementByClientSorted.length} by sessions, sorted by rate</span>}
                >
                  {charts.engagementByClient.loading ? (
                    <ChartSkeleton type="bar" height={420} />
                  ) : charts.engagementByClient.error ? (
                    <ChartError message={charts.engagementByClient.error} onRetry={() => retryChart("engagementByClient")} height={420} />
                  ) : engagementByClientSorted.length === 0 ? (
                    <Empty description="No data" />
                  ) : (
                    <ResponsiveContainer width="100%" height={440}>
                      <ComposedChart data={engagementByClientSorted} margin={{ top: 24, right: 56, bottom: 36, left: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="clientName" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={78} label={xLabel("Client")} />
                        <YAxis
                          yAxisId="left"
                          tick={{ fontSize: 11 }}
                          domain={[0, 1]}
                          tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                          label={yLabel("Engagement rate")}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => Number(v).toLocaleString()}
                          label={{ value: "Sessions", angle: 90, position: "insideRight", offset: 16, style: { fontSize: 11, fill: "#64748b" } }}
                        />
                        <Tooltip
                          formatter={(v, name) => {
                            if (name === "Engagement rate") return `${(Number(v) * 100).toFixed(1)}%`;
                            return Number(v).toLocaleString();
                          }}
                          labelFormatter={(label, payload) => {
                            const row = payload?.[0]?.payload;
                            if (!row) return label;
                            return `${label} — ${Number(row.activeUsers).toLocaleString()} active users`;
                          }}
                        />
                        <Legend wrapperStyle={LEGEND_STYLE} />
                        <Bar yAxisId="left" dataKey="engagementRate" name="Engagement rate" fill="#10b981" radius={[4, 4, 0, 0]}>
                          <LabelList dataKey="engagementRate" position="top" formatter={pctLabel} style={{ fontSize: 10, fill: "#475569" }} />
                        </Bar>
                        <Line yAxisId="right" type="monotone" dataKey="sessions" name="Sessions" stroke="#6366f1" strokeWidth={2} dot={{ r: 3, fill: "#6366f1" }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </Col>

              <Col xs={24}>
                <Card title={<MetricLabel title="Client × Module Group" hint={CHART_HINTS.clientModulePivot} />} size="small">
                  {charts.clientModulePivot.loading ? (
                    <ChartSkeleton type="table" />
                  ) : charts.clientModulePivot.error ? (
                    <ChartError message={charts.clientModulePivot.error} onRetry={() => retryChart("clientModulePivot")} />
                  ) : (clientModulePivot.rows || []).length === 0 ? (
                    <Empty description="No data" />
                  ) : (
                    <Table
                      rowKey="client"
                      dataSource={clientModulePivot.rows}
                      columns={pivotColumns}
                      size="small"
                      bordered
                      pagination={{ pageSize: 10, showSizeChanger: false, size: "small" }}
                      scroll={{ x: "max-content" }}
                    />
                  )}
                </Card>
              </Col>

            </Row>
              </div>
            ),
          },
          {
            key: "compare",
            label: "Compare periods",
            children: compareOpened ? (
              <ComparePeriods
                params={params}
                compareParams={compareParams}
                currentRange={gaFilter.range}
                compareRange={compareRange}
                onAuthError={handleAuthError}
              />
            ) : null,
          },
        ]}
      />

      <div
        onClick={() => setGlossaryOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setGlossaryOpen(true); }}
        className="glossary-tab"
      >
        <BookOutlined />
        Glossary
      </div>

      <Drawer
        title="Metric definitions"
        placement="right"
        width={420}
        open={glossaryOpen}
        onClose={() => setGlossaryOpen(false)}
      >
        <dl className="m-0 text-[13px] leading-[1.6]">
          <dt className="font-semibold">Active users</dt>
          <dd className="m-0 mb-3 text-slate-600">{METRIC_HINTS.activeUsers}</dd>

          <dt className="font-semibold">Total users</dt>
          <dd className="m-0 mb-3 text-slate-600">{METRIC_HINTS.totalUsers}</dd>

          <dt className="font-semibold">New users</dt>
          <dd className="m-0 mb-3 text-slate-600">{METRIC_HINTS.newUsers}</dd>

          <dt className="font-semibold">Returning users</dt>
          <dd className="m-0 mb-3 text-slate-600">Total users minus New users — people who had visited the site before this date range.</dd>

          <dt className="font-semibold">Sessions</dt>
          <dd className="m-0 mb-3 text-slate-600">{METRIC_HINTS.sessions}</dd>

          <dt className="font-semibold">Engaged sessions</dt>
          <dd className="m-0 mb-3 text-slate-600">Sessions ≥ 10s, with 2+ pageviews, or with a conversion event.</dd>

          <dt className="font-semibold">Engagement rate</dt>
          <dd className="m-0 mb-3 text-slate-600">{METRIC_HINTS.engagementRate}</dd>

          <dt className="font-semibold">Bounce rate</dt>
          <dd className="m-0 mb-3 text-slate-600">{METRIC_HINTS.bounceRate}</dd>

          <dt className="font-semibold">Views</dt>
          <dd className="m-0 mb-3 text-slate-600">{METRIC_HINTS.views}</dd>

          <dt className="font-semibold">Avg session duration</dt>
          <dd className="m-0 mb-3 text-slate-600">{METRIC_HINTS.avgSessionDuration}</dd>

          <dt className="font-semibold">Module / Module Group</dt>
          <dd className="m-0 mb-3 text-slate-600">Derived in this app from the page path (web) or the screen name (iOS / Android app) via classification rules.</dd>

          <dt className="font-semibold">Platform</dt>
          <dd className="m-0 mb-3 text-slate-600">Web traffic is split by device category into Desktop, Tablet and Mobile browser. App traffic comes from the iOS and Android app streams. All are selected by default; untick and press Apply to narrow the whole dashboard.</dd>

          <dt className="font-semibold">Tenant / Client Name</dt>
          <dd className="m-0 mb-3 text-slate-600">From the <code>client_name</code> event parameter sent by the SafetyQube web app and mobile app. The app sends the tenant name and the web app currently sends the subdomain; selecting a tenant covers both. Traffic with no tenant tag (“unknown” or not set) is hidden unless “Hide untagged” is unticked. This page is hard-scoped to <code>app_env = production</code>.</dd>
        </dl>
      </Drawer>
    </div>
  );
}
